import { acceptInvitationResponseSchema, accountListSchema, addSendingDomainRequestSchema, addSendingDomainResponseSchema, aiUsageListSchema, apiKeyListSchema, brandProfileSchema, bulkEditResponseSchema, campaignContentSchema, campaignDraftSchema, campaignListSchema, campaignPlanSchema, campaignPreviewRequestSchema, campaignPreviewResponseSchema, campaignReadinessSchema, campaignRecipientPageSchema, campaignReportSchema, clearDuplicatesResponseSchema, columnLabelsSchema, connectGoogleStartSchema, connectMondayResponseSchema, consentPageSchema, contactDetailSchema, contactFieldListSchema, contactPageSchema, contactViewListSchema, conversationListSchema, createCampaignResponseSchema, createdApiKeySchema, currentSessionSchema, dashboardSchema, declareFieldRequestSchema, declareFieldResponseSchema, deleteContactsResponseSchema, designReplySchema, domainCheckResponseSchema, duplicateListSchema, duplicateTemplateResponseSchema, fixNamesResponseSchema, importJobSchema, importResponseSchema, invitationPreviewSchema, inviteResponseSchema, membershipChangedSchema, mondayBoardListSchema, mondayBoardSchema, mondayConnectionSchema, mondaySyncSchema, mondayWebhookSchema, organisationListSchema, organizationSettingsSchema, orgPlanLimitsSchema, platformOverviewSchema, problemDetailsSchema, profileSchema, providerConfigurationSchema, queueActionResponseSchema, queueCampaignResponseSchema, queuedImportSchema, rewriteCampaignResponseSchema, saveContactViewResponseSchema, saveSegmentResponseSchema, saveSegmentViewResponseSchema, saveTemplateResponseSchema, scheduleCampaignResponseSchema, searchResultsSchema, segmentCountResponseSchema, segmentListSchema, segmentMembersSchema, segmentSchema, segmentViewListSchema, sendingDomainListSchema, sendingHistorySchema, sendPageSchema, signUpRequestSchema, signUpResponseSchema, storedConversationSchema, stuckPageSchema, suppressionPageSchema, teamSchema, templateListSchema, testSendCampaignResponseSchema, testSendResponseSchema, ticketListSchema, ticketThreadSchema, unsubscribeTokenSchema, unsuppressResponseSchema, } from '@carbonwebmail/contracts';
/** The prefix each subject's columns live under. */
function fieldsOf(subject) {
    return subject === 'contacts' ? '/api/contact-fields' : '/api/segment-fields';
}
export function createApiClient({ transport }) {
    /**
     * A paged listing. Written once because three of them differ only in a path
     * and a schema, and three copies is three chances to forget the parse.
     */
    async function listing(path, page, schema, 
    /** Extra query parameters. Encoded here so no caller builds a query string. */
    extra = {}) {
        const query = new URLSearchParams({ page: String(page), ...extra });
        const response = await transport(`${path}?${query.toString()}`, { method: 'GET' });
        if (!response.ok)
            throw new Error(`${path} failed: ${response.status}`);
        const body = schema.safeParse(await readJson(response));
        // A 2xx in a shape the contract does not describe means the two halves have
        // drifted. Throwing beats rendering a guess at what it meant.
        if (!body.success)
            throw new Error(`${path} did not match the contract`);
        return body.data;
    }
    return {
        async unsubscribe(token) {
            // Validated here as well as on the server: a malformed token is a broken
            // link, and there is no point spending a round trip to be told so.
            const parsed = unsubscribeTokenSchema.safeParse(token);
            if (!parsed.success)
                return { recorded: false };
            const response = await transport(`/u/${encodeURIComponent(parsed.data)}`, {
                method: 'POST',
                headers: { 'content-type': 'application/x-www-form-urlencoded' },
                // RFC 8058 specifies this body for a one-click unsubscribe. Sending what
                // a mailbox provider would means one path through the server rather
                // than a separate one for the browser.
                body: 'List-Unsubscribe=One-Click',
            });
            return { recorded: response.ok };
        },
        async signUp(request) {
            const parsed = signUpRequestSchema.safeParse(request);
            if (!parsed.success)
                return invalid(parsed.error.issues.map((issue) => issue.message));
            const response = await transport('/api/signup', json(parsed.data));
            if (!response.ok)
                return invalid(await problemsFrom(response));
            const body = signUpResponseSchema.safeParse(await readJson(response));
            // A 2xx whose body is not the agreed shape means the two halves have
            // drifted. Reporting it as invalid rather than as success is the honest
            // answer: nothing here can confirm a code was sent.
            if (!body.success)
                return invalid(['The server sent a response we did not understand.']);
            return body.data;
        },
        async currentSession() {
            const response = await transport('/api/session', { method: 'GET' });
            // 401 is the ordinary answer for a visitor who is not signed in, so it is
            // "no session" rather than an error to report.
            if (!response.ok)
                return undefined;
            const body = currentSessionSchema.safeParse(await readJson(response));
            return body.success ? body.data : undefined;
        },
        async queryContacts(query) {
            const response = await transport('/api/contacts/query', json(query));
            if (!response.ok)
                throw new Error(`contacts query failed: ${response.status}`);
            const body = contactPageSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('contacts response did not match the contract');
            return body.data;
        },
        async listContacts(query) {
            const params = new URLSearchParams({ page: String(query.page) });
            // Omitted rather than sent empty, so the request says what it means and
            // a log of it reads the same way.
            if (query.search !== '')
                params.set('search', query.search);
            if (query.view !== undefined)
                params.set('view', query.view);
            // Short enough to sit in the url, which is the point: the order a board
            // is in is part of what somebody means when they share a link to it.
            if (query.sort !== undefined) {
                params.set('sort', query.sort.column === 'field'
                    ? `field:${query.sort.key ?? ''}:${query.sort.direction}`
                    : `${query.sort.column}:${query.sort.direction}`);
            }
            const response = await transport(`/api/contacts?${params.toString()}`, { method: 'GET' });
            if (!response.ok)
                throw new Error(`contacts request failed: ${response.status}`);
            const body = contactPageSchema.safeParse(await readJson(response));
            // A 2xx in a shape the contract does not describe means the two halves
            // have drifted. Throwing beats rendering a guess at what it meant.
            if (!body.success)
                throw new Error('contacts response did not match the contract');
            return body.data;
        },
        async contact(contactId) {
            const response = await transport(`/api/contacts/${encodeURIComponent(contactId)}`, {
                method: 'GET',
            });
            if (!response.ok)
                throw new Error(`contact request failed: ${response.status}`);
            const body = contactDetailSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('contact response did not match the contract');
            return body.data;
        },
        async dashboard() {
            const response = await transport('/api/dashboard', { method: 'GET' });
            if (!response.ok)
                throw new Error(`dashboard request failed: ${response.status}`);
            const body = dashboardSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('dashboard response did not match the contract');
            return body.data;
        },
        async platformOverview() {
            const response = await transport('/api/admin', { method: 'GET' });
            if (!response.ok)
                throw new Error(`platform overview request failed: ${response.status}`);
            const body = platformOverviewSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('platform overview did not match the contract');
            return body.data;
        },
        async platformAccounts(page) {
            const response = await transport(`/api/admin/accounts?page=${page}`, { method: 'GET' });
            if (!response.ok)
                throw new Error(`accounts request failed: ${response.status}`);
            const body = accountListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the account list did not match the contract');
            return body.data.accounts;
        },
        async platformOrganisations(page) {
            const response = await transport(`/api/admin/organisations?page=${page}`, { method: 'GET' });
            if (!response.ok)
                throw new Error(`organisations request failed: ${response.status}`);
            const body = organisationListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the organisation list did not match the contract');
            return body.data.organisations;
        },
        async platformAiUsage(days) {
            const response = await transport(`/api/admin/ai-usage?days=${days}`, { method: 'GET' });
            if (!response.ok)
                throw new Error(`ai usage request failed: ${response.status}`);
            const body = aiUsageListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the usage figures did not match the contract');
            return body.data;
        },
        async addContact(contact, segmentIds) {
            const result = await this.importContacts({
                source: ADDED_BY_HAND,
                rows: [contact],
                ...(segmentIds === undefined || segmentIds.length === 0
                    ? {}
                    : { segmentIds: [...segmentIds] }),
            });
            if (result.rejected.length > 0) {
                throw new Error(result.rejected[0]?.reason ?? 'that contact was refused');
            }
            return result.added === 1;
        },
        async importContacts(request) {
            const response = await transport('/api/contacts/import', json(request));
            if (!response.ok)
                throw new Error(`import batch failed: ${response.status}`);
            const body = importResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('import response did not match the contract');
            return body.data;
        },
        async campaignReadiness(campaignId) {
            const response = await transport(`/api/campaigns/${encodeURIComponent(campaignId)}/readiness`, { method: 'GET' });
            if (!response.ok)
                throw new Error(`readiness request failed: ${response.status}`);
            const body = campaignReadinessSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the readiness did not match the contract');
            return body.data;
        },
        async testSendCampaign(campaignId) {
            const response = await transport(`/api/campaigns/${encodeURIComponent(campaignId)}/test`, {
                method: 'POST',
            });
            if (!response.ok)
                throw new Error(`test send failed: ${response.status}`);
            const body = testSendCampaignResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the test send did not match the contract');
            return body.data;
        },
        async findAnything(term) {
            const response = await transport(`/api/search?q=${encodeURIComponent(term)}`, {
                method: 'GET',
            });
            if (!response.ok)
                throw new Error(`search failed: ${response.status}`);
            const body = searchResultsSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the search results did not match the contract');
            return body.data;
        },
        async editContact(contactId, request) {
            const response = await transport(`/api/contacts/${encodeURIComponent(contactId)}`, {
                ...json(request),
                method: 'PUT',
            });
            if (!response.ok)
                throw new Error(`saving that contact failed: ${response.status}`);
        },
        async fixContactNames(placeholder) {
            const response = await transport('/api/contacts/fix-names', json(placeholder === undefined ? {} : { placeholder }));
            if (!response.ok)
                throw new Error(`fixing those names failed: ${response.status}`);
            const body = fixNamesResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the fix-names response did not match the contract');
            return body.data.changed;
        },
        async editContacts(contactIds, change) {
            const response = await transport('/api/contacts/edit', json({ contactIds: [...contactIds], change }));
            if (!response.ok)
                throw new Error(`editing those contacts failed: ${response.status}`);
            const body = bulkEditResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the bulk edit response did not match the contract');
            return body.data.changed;
        },
        async deleteContacts(contactIds) {
            const response = await transport('/api/contacts/delete', json({ contactIds: [...contactIds] }));
            if (!response.ok)
                throw new Error(`deleting contacts failed: ${response.status}`);
            const body = deleteContactsResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the delete response did not match the contract');
            return body.data.removed;
        },
        async columnLabels(subject) {
            const response = await transport(`${fieldsOf(subject)}/labels`, { method: 'GET' });
            if (!response.ok)
                throw new Error(`column labels failed: ${response.status}`);
            const body = columnLabelsSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the column labels did not match the contract');
            return body.data;
        },
        async renameColumn(subject, columnId, label) {
            const path = `${fieldsOf(subject)}/labels/${encodeURIComponent(columnId)}`;
            // No label is "put it back to what it was called", which is deleting the
            // override rather than writing the default in — so a default that changes
            // later still reaches this organisation.
            const response = await transport(path, label === undefined ? { method: 'DELETE' } : { ...json({ label }), method: 'PUT' });
            if (!response.ok)
                throw new Error(`renaming that column failed: ${response.status}`);
        },
        async editField(subject, key, request) {
            const response = await transport(`${fieldsOf(subject)}/${encodeURIComponent(key)}`, {
                ...json(request),
                method: 'PUT',
            });
            if (!response.ok)
                throw new Error(`saving that column failed: ${response.status}`);
            const body = declareFieldResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the save response did not match the contract');
            return body.data;
        },
        async contactViews() {
            const response = await transport('/api/contact-views', { method: 'GET' });
            if (!response.ok)
                throw new Error(`views request failed: ${response.status}`);
            const body = contactViewListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the views did not match the contract');
            return body.data;
        },
        async saveContactView(request, viewId) {
            const response = await transport(viewId === undefined
                ? '/api/contact-views'
                : `/api/contact-views/${encodeURIComponent(viewId)}`, { ...json(request), method: viewId === undefined ? 'POST' : 'PUT' });
            if (!response.ok)
                throw new Error(`saving a view failed: ${response.status}`);
            const body = saveContactViewResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the save response did not match the contract');
            return body.data;
        },
        async removeContactView(viewId) {
            const response = await transport(`/api/contact-views/${encodeURIComponent(viewId)}`, {
                method: 'DELETE',
            });
            if (!response.ok)
                throw new Error(`deleting a view failed: ${response.status}`);
        },
        async segmentViews() {
            const response = await transport('/api/segment-views', { method: 'GET' });
            if (!response.ok)
                throw new Error(`segment views request failed: ${response.status}`);
            const body = segmentViewListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the segment views did not match the contract');
            return body.data;
        },
        async saveSegmentView(request, viewId) {
            const response = await transport(viewId === undefined
                ? '/api/segment-views'
                : `/api/segment-views/${encodeURIComponent(viewId)}`, { ...json(request), method: viewId === undefined ? 'POST' : 'PUT' });
            if (!response.ok)
                throw new Error(`saving a segment view failed: ${response.status}`);
            const body = saveSegmentViewResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the save response did not match the contract');
            return body.data;
        },
        async removeSegmentView(viewId) {
            const response = await transport(`/api/segment-views/${encodeURIComponent(viewId)}`, {
                method: 'DELETE',
            });
            if (!response.ok)
                throw new Error(`deleting a segment view failed: ${response.status}`);
        },
        async duplicates() {
            const response = await transport('/api/contacts/duplicates', { method: 'GET' });
            if (!response.ok)
                throw new Error(`duplicates request failed: ${response.status}`);
            const body = duplicateListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the duplicates did not match the contract');
            return body.data;
        },
        async clearDuplicates(request) {
            const response = await transport('/api/contacts/duplicates/clear', json(request));
            if (!response.ok)
                throw new Error(`clearing duplicates failed: ${response.status}`);
            const body = clearDuplicatesResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the clear response did not match the contract');
            return body.data;
        },
        async emailProvider() {
            const response = await transport('/api/email-provider', { method: 'GET' });
            if (!response.ok)
                throw new Error(`provider request failed: ${response.status}`);
            const body = providerConfigurationSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the provider configuration did not match the contract');
            return body.data;
        },
        async connectGoogleUrl() {
            const response = await transport('/api/google/start', { method: 'GET' });
            // 501 is the deployment having no Google client, which is ordinary and
            // not a failure to report — the screen offers something else instead.
            if (!response.ok)
                return undefined;
            const body = connectGoogleStartSchema.safeParse(await readJson(response));
            return body.success ? body.data.url : undefined;
        },
        async saveEmailProvider(request) {
            const response = await transport('/api/email-provider', { ...json(request), method: 'PUT' });
            if (response.ok)
                return { status: 'changed' };
            // The server lists every missing field at once, and that sentence is the
            // whole value of the refusal — a generic line would send somebody back to
            // guess which box was empty.
            const problems = await problemsFrom(response);
            return { status: 'refused', problem: problems[0] ?? 'We could not save that.' };
        },
        async clearEmailProvider() {
            const response = await transport('/api/email-provider', { method: 'DELETE' });
            if (!response.ok)
                throw new Error(`clearing the provider failed: ${response.status}`);
        },
        async sendTestEmail() {
            const response = await transport('/api/email-provider/test', { method: 'POST' });
            if (!response.ok)
                throw new Error(`test send failed: ${response.status}`);
            const body = testSendResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the test send did not match the contract');
            return body.data;
        },
        async apiKeys() {
            const response = await transport('/api/api-keys', { method: 'GET' });
            if (!response.ok)
                throw new Error(`api key request failed: ${response.status}`);
            const body = apiKeyListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the api keys did not match the contract');
            return body.data;
        },
        async createApiKey(request) {
            const response = await transport('/api/api-keys', json(request));
            if (!response.ok)
                throw new Error(`creating an api key failed: ${response.status}`);
            const body = createdApiKeySchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the created key did not match the contract');
            return body.data;
        },
        async revokeApiKey(id) {
            const response = await transport(`/api/api-keys/${encodeURIComponent(id)}`, {
                method: 'DELETE',
            });
            if (!response.ok)
                throw new Error(`revoking an api key failed: ${response.status}`);
        },
        async team() {
            const response = await transport('/api/team', { method: 'GET' });
            if (!response.ok)
                throw new Error(`team request failed: ${response.status}`);
            const body = teamSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the team did not match the contract');
            return body.data;
        },
        async invite(request) {
            const response = await transport('/api/team/invitations', json(request));
            if (!response.ok)
                throw new Error(`inviting failed: ${response.status}`);
            const body = inviteResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the invite response did not match the contract');
            return body.data;
        },
        async revokeInvitation(email) {
            const response = await transport(`/api/team/invitations/${encodeURIComponent(email)}`, {
                method: 'DELETE',
            });
            if (!response.ok)
                throw new Error(`revoking an invitation failed: ${response.status}`);
        },
        async setMemberRole(userId, request) {
            const response = await transport(`/api/team/members/${encodeURIComponent(userId)}/role`, {
                ...json(request),
                method: 'PUT',
            });
            if (!response.ok)
                throw new Error(`changing a role failed: ${response.status}`);
        },
        async removeMember(userId) {
            const response = await transport(`/api/team/members/${encodeURIComponent(userId)}`, {
                method: 'DELETE',
            });
            if (!response.ok)
                throw new Error(`removing a member failed: ${response.status}`);
        },
        async invitation(token) {
            const response = await transport(`/api/join/${encodeURIComponent(token)}`, { method: 'GET' });
            // 404 is the answer rather than a failure: expired, used, or never real.
            if (response.status === 404)
                return undefined;
            if (!response.ok)
                throw new Error(`invitation request failed: ${response.status}`);
            const body = invitationPreviewSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the invitation did not match the contract');
            return body.data;
        },
        async acceptInvitation(token) {
            const response = await transport(`/api/join/${encodeURIComponent(token)}`, json({}));
            if (!response.ok)
                throw new Error(`accepting an invitation failed: ${response.status}`);
            const body = acceptInvitationResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the accept response did not match the contract');
            return body.data;
        },
        async profile() {
            const response = await transport('/api/profile', { method: 'GET' });
            if (!response.ok)
                throw new Error(`profile request failed: ${response.status}`);
            const body = profileSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the profile did not match the contract');
            return body.data;
        },
        async saveProfileName(name) {
            const response = await transport('/api/profile/name', { ...json({ name }), method: 'PUT' });
            if (!response.ok)
                throw new Error(`saving your name failed: ${response.status}`);
        },
        async settings() {
            const response = await transport('/api/settings', { method: 'GET' });
            if (!response.ok)
                throw new Error(`settings request failed: ${response.status}`);
            const body = organizationSettingsSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('settings did not match the contract');
            return body.data;
        },
        async plan() {
            const response = await transport('/api/settings/plan', { method: 'GET' });
            if (!response.ok)
                throw new Error(`plan request failed: ${response.status}`);
            const body = orgPlanLimitsSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the plan did not match the contract');
            return body.data;
        },
        async tickets() {
            const response = await transport('/api/support', { method: 'GET' });
            if (!response.ok)
                throw new Error('tickets request failed: ' + response.status);
            const body = ticketListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the tickets did not match the contract');
            return body.data;
        },
        async ticket(ticketId) {
            const response = await transport('/api/support/' + ticketId, { method: 'GET' });
            if (!response.ok)
                throw new Error('ticket request failed: ' + response.status);
            const body = ticketThreadSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the ticket did not match the contract');
            return body.data;
        },
        async openTicket(request) {
            const response = await transport('/api/support', { ...json(request), method: 'POST' });
            if (!response.ok)
                throw new Error('opening a ticket failed: ' + response.status);
            const body = (await readJson(response));
            if (typeof body.id !== 'string')
                throw new Error('opening a ticket returned no id');
            return { id: body.id };
        },
        async replyToTicket(ticketId, body) {
            const response = await transport('/api/support/' + ticketId + '/replies', {
                ...json({ body }),
                method: 'POST',
            });
            if (!response.ok)
                throw new Error('replying failed: ' + response.status);
        },
        async closeTicket(ticketId) {
            const response = await transport('/api/support/' + ticketId + '/close', { method: 'POST' });
            if (!response.ok)
                throw new Error('closing the ticket failed: ' + response.status);
        },
        async saveSettings(settings) {
            const response = await transport('/api/settings', { ...json(settings), method: 'PUT' });
            if (!response.ok)
                throw new Error(`saving settings failed: ${response.status}`);
            const body = organizationSettingsSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('settings did not match the contract');
            return body.data;
        },
        async declaredFields(subject) {
            const response = await transport(fieldsOf(subject), { method: 'GET' });
            if (!response.ok)
                throw new Error(`${subject} fields request failed: ${response.status}`);
            const body = contactFieldListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error(`${subject} fields did not match the contract`);
            return body.data;
        },
        async declareField(subject, request) {
            const parsed = declareFieldRequestSchema.safeParse(request);
            if (!parsed.success) {
                // Both sides hold the same schema, so a request this one rejects is
                // one the server would reject too — answered here rather than in a
                // round trip.
                return {
                    status: 'invalid',
                    problem: parsed.error.issues[0]?.message ?? 'that field cannot be added',
                };
            }
            const response = await transport(fieldsOf(subject), json(parsed.data));
            if (!response.ok)
                throw new Error(`declaring a field failed: ${response.status}`);
            const body = declareFieldResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('declare response did not match the contract');
            return body.data;
        },
        async deleteField(subject, key) {
            const response = await transport(`${fieldsOf(subject)}/${encodeURIComponent(key)}`, {
                method: 'DELETE',
            });
            if (response.status === 204)
                return 'deleted';
            if (response.status === 404)
                return 'not-found';
            throw new Error(`deleting a field failed: ${response.status}`);
        },
        async mondayConnection() {
            const response = await transport('/api/monday/connection', { method: 'GET' });
            if (!response.ok)
                throw new Error(`the monday connection failed: ${response.status}`);
            const body = mondayConnectionSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the monday connection did not match the contract');
            return body.data;
        },
        async connectMonday(token) {
            const response = await transport('/api/monday/connection', json({ token }));
            if (!response.ok)
                throw new Error(`connecting monday failed: ${response.status}`);
            const body = connectMondayResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the connect response did not match the contract');
            return body.data;
        },
        async disconnectMonday() {
            const response = await transport('/api/monday/connection', { method: 'DELETE' });
            // 404 is already disconnected, which is the state the caller wanted.
            if (!response.ok && response.status !== 404) {
                throw new Error(`disconnecting monday failed: ${response.status}`);
            }
        },
        async mondayBoards() {
            const response = await transport('/api/monday/boards', { method: 'GET' });
            if (!response.ok)
                throw new Error(`the monday boards failed: ${response.status}`);
            const body = mondayBoardListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the monday boards did not match the contract');
            return body.data;
        },
        async mondayBoard(boardId) {
            const response = await transport(`/api/monday/boards/${encodeURIComponent(boardId)}`, {
                method: 'GET',
            });
            if (response.status === 404)
                return undefined;
            if (!response.ok)
                throw new Error(`that monday board failed: ${response.status}`);
            const body = mondayBoardSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('that monday board did not match the contract');
            return body.data;
        },
        async mondaySync(boardId) {
            const path = `/api/monday/boards/${encodeURIComponent(boardId)}/sync`;
            const response = await transport(path, { method: 'GET' });
            // Not there means the board has never been imported, which is a state the
            // screen shows rather than a failure.
            if (response.status === 404)
                return undefined;
            if (!response.ok)
                throw new Error(`that board's sync failed: ${response.status}`);
            const body = mondaySyncSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the sync settings did not match the contract');
            return body.data;
        },
        async setMondaySync(boardId, sync) {
            const path = `/api/monday/boards/${encodeURIComponent(boardId)}/sync`;
            const response = await transport(path, { ...json(sync), method: 'PUT' });
            if (!response.ok)
                throw new Error(`saving the sync settings failed: ${response.status}`);
        },
        async newMondayWebhook(boardId) {
            const path = `/api/monday/boards/${encodeURIComponent(boardId)}/webhook`;
            const response = await transport(path, { method: 'POST' });
            if (!response.ok)
                throw new Error(`making a webhook URL failed: ${response.status}`);
            const body = mondayWebhookSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the server answered with a shape this client does not know');
            return body.data.webhookUrl;
        },
        async importFromMonday(request) {
            const response = await transport('/api/monday/import', json(request));
            // 400 carries the mapping problem the screen shows beside the columns.
            if (response.status === 400) {
                const problem = await readJson(response);
                const detail = typeof problem === 'object' && problem !== null && 'detail' in problem
                    ? String(problem.detail)
                    : 'That mapping cannot be imported with.';
                return { refused: detail };
            }
            if (!response.ok)
                throw new Error(`starting the import failed: ${response.status}`);
            const body = queuedImportSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the queued import did not match the contract');
            return body.data;
        },
        async mondayImportProgress(jobId) {
            const path = `/api/monday/${encodeURIComponent(jobId)}/progress`;
            const response = await transport(path);
            if (response.status === 404)
                return undefined;
            if (!response.ok)
                throw new Error(`reading the import failed: ${response.status}`);
            const body = importJobSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the import progress did not match the contract');
            return body.data;
        },
        async campaigns() {
            const response = await transport('/api/campaigns', { method: 'GET' });
            if (!response.ok)
                throw new Error(`campaigns request failed: ${response.status}`);
            const body = campaignListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('campaigns response did not match the contract');
            return body.data;
        },
        async campaignReport(campaignId) {
            const response = await transport(`/api/campaigns/${encodeURIComponent(campaignId)}/report`, {
                method: 'GET',
            });
            if (!response.ok)
                throw new Error(`campaign report failed: ${response.status}`);
            const body = campaignReportSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('campaign report did not match the contract');
            return body.data;
        },
        async campaignRecipients(campaignId, page, state) {
            return listing(`/api/campaigns/${encodeURIComponent(campaignId)}/recipients`, page, campaignRecipientPageSchema, state === undefined ? {} : { state });
        },
        async campaignPreview(request) {
            const parsed = campaignPreviewRequestSchema.safeParse(request);
            // Nothing to render yet is the normal state of an editor, so this is not
            // an error — the caller shows no preview rather than a failure.
            if (!parsed.success)
                throw new Error('there is not enough here to preview');
            const response = await transport('/api/campaigns/preview', json(parsed.data));
            if (!response.ok)
                throw new Error(`campaign preview failed: ${response.status}`);
            const body = campaignPreviewResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('preview response did not match the contract');
            return body.data;
        },
        async campaignContent(campaignId) {
            const response = await transport(`/api/campaigns/${encodeURIComponent(campaignId)}/content`, {
                method: 'GET',
            });
            if (!response.ok)
                throw new Error(`campaign content failed: ${response.status}`);
            const body = campaignContentSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('campaign content did not match the contract');
            return body.data;
        },
        async rewriteCampaign(campaignId, request) {
            const parsed = campaignDraftSchema.safeParse(request);
            // Refused here rather than round-tripping: both sides hold the same
            // schema, so a body this one rejects is one the server would too.
            if (!parsed.success)
                throw new Error('that campaign cannot be saved as written');
            const response = await transport(`/api/campaigns/${encodeURIComponent(campaignId)}/content`, {
                ...json(parsed.data),
                method: 'PUT',
            });
            if (!response.ok)
                throw new Error(`rewriting a campaign failed: ${response.status}`);
            const body = rewriteCampaignResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('rewrite response did not match the contract');
            return body.data;
        },
        async createCampaign(request) {
            const parsed = campaignDraftSchema.safeParse(request);
            if (!parsed.success)
                return invalid(parsed.error.issues.map((issue) => issue.message));
            const response = await transport('/api/campaigns', json(parsed.data));
            if (!response.ok)
                return invalid(await problemsFrom(response));
            const body = createCampaignResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                return invalid(['The server sent a response we did not understand.']);
            return { status: 'created', campaignId: body.data.campaignId };
        },
        async scheduleCampaign(campaignId, schedule) {
            const path = `/api/campaigns/${encodeURIComponent(campaignId)}/schedule`;
            const response = await transport(path, json({ schedule }));
            if (response.status === 404)
                return 'not-found';
            if (!response.ok)
                throw new Error(`scheduling a campaign failed: ${response.status}`);
            const body = scheduleCampaignResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('schedule response did not match the contract');
            return body.data;
        },
        async unscheduleCampaign(campaignId) {
            const path = `/api/campaigns/${encodeURIComponent(campaignId)}/schedule`;
            const response = await transport(path, { method: 'DELETE' });
            if (response.status === 404)
                return false;
            if (!response.ok)
                throw new Error(`unscheduling a campaign failed: ${response.status}`);
            return true;
        },
        async queueCampaign(campaignId) {
            const response = await transport(`/api/campaigns/${encodeURIComponent(campaignId)}/queue`, {
                method: 'POST',
            });
            if (response.status === 404)
                return { status: 'not-found' };
            if (!response.ok)
                throw new Error(`queueing a campaign failed: ${response.status}`);
            const body = queueCampaignResponseSchema.safeParse(await readJson(response));
            // Throwing beats guessing. The alternative is telling somebody their
            // campaign is sending on the strength of a body nobody could read.
            if (!body.success)
                throw new Error('queue response did not match the contract');
            return body.data;
        },
        async segments() {
            const response = await transport('/api/segments', { method: 'GET' });
            if (!response.ok)
                throw new Error(`segments request failed: ${response.status}`);
            const body = segmentListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('segments response did not match the contract');
            return body.data;
        },
        async segment(segmentId) {
            const response = await transport(`/api/segments/${encodeURIComponent(segmentId)}`, {
                method: 'GET',
            });
            if (response.status === 404)
                return undefined;
            if (!response.ok)
                throw new Error(`that segment could not be loaded: ${response.status}`);
            const body = segmentSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the segment did not match the contract');
            return body.data;
        },
        async saveSegment(request, segmentId) {
            const response = await transport(segmentId === undefined ? '/api/segments' : `/api/segments/${segmentId}`, { ...json(request), ...(segmentId === undefined ? {} : { method: 'PUT' }) });
            if (!response.ok)
                throw new Error(`saving a segment failed: ${response.status}`);
            const body = saveSegmentResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('save response did not match the contract');
            return body.data;
        },
        async deleteCampaign(campaignId) {
            const response = await transport(`/api/campaigns/${encodeURIComponent(campaignId)}`, {
                method: 'DELETE',
            });
            if (response.status === 204)
                return 'deleted';
            // 409 is the campaign having been sent, which the caller shows as a
            // reason rather than as a failure.
            if (response.status === 409)
                return 'sent';
            if (response.status === 404)
                return 'not-found';
            throw new Error(`deleting a campaign failed: ${response.status}`);
        },
        async changeSegmentMembers(segmentId, change, connecting) {
            const response = await transport(`/api/segments/${encodeURIComponent(segmentId)}/members`, {
                ...json(change),
                method: connecting ? 'POST' : 'DELETE',
            });
            if (!response.ok)
                throw new Error(`changing segment members failed: ${response.status}`);
            const body = membershipChangedSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the membership response did not match the contract');
            return body.data.changed;
        },
        async segmentMembers(segmentId, page) {
            return listing(`/api/segments/${encodeURIComponent(segmentId)}/members`, page, segmentMembersSchema);
        },
        async exportSegment(segmentId) {
            const response = await transport(`/api/segments/${encodeURIComponent(segmentId)}/export`, {
                method: 'GET',
            });
            if (!response.ok)
                throw new Error(`segment export failed: ${response.status}`);
            // Text, not JSON: it is a file, and parsing it here would be turning it
            // back into something it is not.
            return response.text();
        },
        async deleteSegment(segmentId) {
            const response = await transport(`/api/segments/${segmentId}`, { method: 'DELETE' });
            if (response.status === 204)
                return 'deleted';
            // 409 is the segment still being part of a sent campaign's record, which
            // the caller shows as a reason rather than as a failure.
            if (response.status === 409)
                return 'in-use';
            if (response.status === 404)
                return 'not-found';
            throw new Error(`deleting a segment failed: ${response.status}`);
        },
        async segmentReach(definition) {
            const response = await transport('/api/segments/reach', json(definition));
            if (!response.ok)
                throw new Error(`segment reach failed: ${response.status}`);
            const body = segmentCountResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('reach response did not match the contract');
            return body.data.reach;
        },
        async templates() {
            const response = await transport('/api/templates', { method: 'GET' });
            if (!response.ok)
                throw new Error(`templates request failed: ${response.status}`);
            const body = templateListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('templates response did not match the contract');
            return body.data;
        },
        async saveTemplate(request, templateId) {
            const response = await transport(templateId === undefined ? '/api/templates' : `/api/templates/${templateId}`, { ...json(request), ...(templateId === undefined ? {} : { method: 'PUT' }) });
            if (!response.ok)
                throw new Error(`saving a template failed: ${response.status}`);
            const body = saveTemplateResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('save response did not match the contract');
            return body.data;
        },
        async duplicateTemplate(templateId) {
            const response = await transport(`/api/templates/${encodeURIComponent(templateId)}/duplicate`, { method: 'POST' });
            // Somebody else deleted it between the list loading and the button being
            // pressed. Said rather than thrown: the card is simply gone.
            if (response.status === 404)
                return 'not-found';
            if (!response.ok)
                throw new Error(`duplicating a template failed: ${response.status}`);
            const body = duplicateTemplateResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('duplicating a template did not match the contract');
            return body.data;
        },
        async deleteTemplate(templateId) {
            const response = await transport(`/api/templates/${templateId}`, { method: 'DELETE' });
            if (response.status === 204)
                return 'deleted';
            if (response.status === 404)
                return 'not-found';
            throw new Error(`deleting a template failed: ${response.status}`);
        },
        async stuck(status, page) {
            return listing(`/api/queue/${status}`, page, stuckPageSchema);
        },
        async replayDead(queueEntryId) {
            return queueAction(transport, `/api/queue/dead/${encodeURIComponent(queueEntryId)}/replay`, undefined);
        },
        async resolveUnknown(queueEntryId, as) {
            return queueAction(transport, `/api/queue/unknown/${encodeURIComponent(queueEntryId)}/resolve`, { as });
        },
        async sendingHistory() {
            const response = await transport('/api/deliverability/history', { method: 'GET' });
            if (!response.ok)
                throw new Error(`sending history failed: ${response.status}`);
            const body = sendingHistorySchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('sending history did not match the contract');
            return body.data;
        },
        async sendingDomains() {
            const response = await transport('/api/sending-domains', { method: 'GET' });
            if (!response.ok)
                throw new Error(`sending domains failed: ${response.status}`);
            const body = sendingDomainListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('sending domains did not match the contract');
            return body.data;
        },
        async addSendingDomain(request) {
            const parsed = addSendingDomainRequestSchema.safeParse(request);
            // Both sides hold the same schema, so a value this one rejects is one
            // the server would reject too.
            if (!parsed.success)
                throw new Error(parsed.error.issues[0]?.message ?? 'that is not a domain');
            const response = await transport('/api/sending-domains', json(parsed.data));
            if (!response.ok)
                throw new Error(`adding a domain failed: ${response.status}`);
            const body = addSendingDomainResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('add response did not match the contract');
            return body.data;
        },
        async deleteSendingDomain(domain) {
            const response = await transport(`/api/sending-domains/${encodeURIComponent(domain)}`, {
                method: 'DELETE',
            });
            if (response.status === 204)
                return 'deleted';
            if (response.status === 404)
                return 'not-found';
            throw new Error(`removing a domain failed: ${response.status}`);
        },
        async checkSendingDomain(domain) {
            const response = await transport(`/api/sending-domains/${encodeURIComponent(domain)}/check`, {
                method: 'POST',
            });
            if (!response.ok)
                throw new Error(`checking a domain failed: ${response.status}`);
            const body = domainCheckResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('check response did not match the contract');
            return body.data;
        },
        async planCampaign(brief) {
            const response = await transport('/api/designer/plan', json({ brief }));
            // 501 is off, 503 is busy, and the form does the same thing for both:
            // nothing. Distinguishing them here would mean a sentence somebody has
            // to read before typing the name they were about to type anyway.
            if (!response.ok)
                return undefined;
            const body = campaignPlanSchema.safeParse(await readJson(response));
            return body.success ? body.data : undefined;
        },
        async designConversations() {
            const response = await transport('/api/designer/conversations');
            if (!response.ok)
                throw new Error(`listing conversations failed: ${response.status}`);
            const body = conversationListSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the conversation list did not match the contract');
            return body.data.conversations;
        },
        async designConversation(conversationId) {
            const path = `/api/designer/${encodeURIComponent(conversationId)}`;
            const response = await transport(path);
            if (response.status === 404)
                return undefined;
            if (!response.ok)
                throw new Error(`reading a conversation failed: ${response.status}`);
            const body = storedConversationSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the conversation did not match the contract');
            return body.data;
        },
        async forgetDesignConversation(conversationId) {
            const path = `/api/designer/${encodeURIComponent(conversationId)}`;
            const response = await transport(path, { method: 'DELETE' });
            if (response.status === 404)
                return false;
            if (!response.ok)
                throw new Error(`removing a conversation failed: ${response.status}`);
            return true;
        },
        async brandProfile() {
            const response = await transport('/api/designer/brand');
            if (!response.ok)
                throw new Error(`reading the brand failed: ${response.status}`);
            const body = brandProfileSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the brand did not match the contract');
            return body.data.brand;
        },
        async forgetBrandProfile() {
            const response = await transport('/api/designer/brand', { method: 'DELETE' });
            if (response.status === 404)
                return false;
            if (!response.ok)
                throw new Error(`forgetting the brand failed: ${response.status}`);
            return true;
        },
        async designEmail(request) {
            const response = await transport('/api/designer', json(request));
            // Not switched on, which no amount of retrying changes.
            if (response.status === 501)
                return 'not-configured';
            // The model was busy. Nothing is wrong with what was asked.
            if (response.status === 503)
                return 'busy';
            /*
             * A file the server would not take. The browser checks the same three
             * things before uploading, so this is a backstop — but when it does
             * happen the server's sentence names the file and the reason, and
             * replacing it with "something went wrong" would throw away the only
             * part worth reading.
             */
            /*
             * A turn the server would not take, in its own words.
             *
             * 413 and 415 are a file; 422 is a website that could not be read — a
             * private address, something that is not a page, a host that never
             * answered. Each carries a sentence naming what and why, which would
             * otherwise arrive as "something went wrong".
             *
             * 502 is deliberately *not* here. That is what a gateway returns when
             * this API is down, and reading it as a refusal told somebody their
             * website had been rejected when the server never saw the request.
             */
            if ([413, 415, 422].includes(response.status)) {
                const problem = await readJson(response);
                const detail = typeof problem === 'object' && problem !== null && 'detail' in problem
                    ? String(problem.detail)
                    : 'That could not be used.';
                return { refused: detail };
            }
            if (!response.ok)
                throw new Error(`designing an email failed: ${response.status}`);
            const body = designReplySchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the designer did not match the contract');
            return body.data;
        },
        async suppressions(page, reason) {
            return listing('/api/deliverability/suppressions', page, suppressionPageSchema, reason === undefined ? {} : { reason });
        },
        async suppress(request) {
            const response = await transport('/api/deliverability/suppressions', json(request));
            if (!response.ok)
                throw new Error(`suppressing failed: ${response.status}`);
        },
        async unsuppress(email) {
            const response = await transport(`/api/deliverability/suppressions/${encodeURIComponent(email)}`, { method: 'DELETE' });
            if (!response.ok)
                throw new Error(`removing a suppression failed: ${response.status}`);
            const body = unsuppressResponseSchema.safeParse(await readJson(response));
            if (!body.success)
                throw new Error('the removal did not match the contract');
            return body.data;
        },
        async sends(page) {
            return listing('/api/deliverability/sends', page, sendPageSchema);
        },
        async consent(page) {
            return listing('/api/deliverability/consent', page, consentPageSchema);
        },
    };
}
/**
 * The two queue actions, which differ only in a path and a body.
 *
 * Written once so they cannot drift into reporting differently: both answer
 * "done" or "stale", and "stale" is the row having moved since the screen drew
 * it — an answer rather than a failure.
 */
async function queueAction(transport, path, body) {
    const response = await transport(path, body === undefined ? { method: 'POST' } : json(body));
    if (!response.ok)
        throw new Error(`${path} failed: ${response.status}`);
    const parsed = queueActionResponseSchema.safeParse(await readJson(response));
    if (!parsed.success)
        throw new Error('the queue action response did not match the contract');
    return parsed.data;
}
function json(body) {
    return {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    };
}
function invalid(problems) {
    return { status: 'invalid', problems };
}
async function readJson(response) {
    try {
        return await response.json();
    }
    catch {
        return undefined;
    }
}
/**
 * Turns an RFC 9457 problem document into lines a person can read.
 *
 * Falls back to a generic line rather than showing a status code: "422" tells
 * someone filling in a form nothing they can act on.
 */
async function problemsFrom(response) {
    const problem = problemDetailsSchema.safeParse(await readJson(response));
    if (!problem.success)
        return ['We could not complete that. Please try again.'];
    const errors = problem.data.errors ?? [];
    if (errors.length > 0)
        return errors.map((issue) => issue.message);
    return [problem.data.detail ?? 'We could not complete that. Please try again.'];
}
/**
 * A transport for the browser.
 *
 * There is one origin. The API service serves this bundle, and in development
 * Vite proxies `/api` and `/u` to it — so the browser wants relative requests,
 * which is what the default empty base gives it. The parameter exists for the
 * in-process case, a test driving a Hono app, where there is no origin at all.
 *
 * It was once the other way round, and the failure was silent: with the app on
 * a static host, an unsubscribe POST went to the static host rather than the
 * API, came back 200 because that host answers every path so the SPA can route
 * it, and the page told people they had been unsubscribed while nothing was
 * recorded. The fix then was to demand an explicit origin. The fix now is that
 * there is only one.
 *
 * **The credential is a header, not a cookie.** It was a cookie, and the swap
 * is not cosmetic: a cookie is attached by the browser to any request to this
 * origin, including one another site caused, which is what CSRF is.
 * `SameSite=Lax` held that off; a header sent only by this code needs no such
 * defence, because nothing else can set it.
 */
export function createFetchTransport(token = async () => undefined, baseUrl = '', fetch = globalThis.fetch) {
    return async (path, init) => {
        const bearer = await token();
        if (bearer === undefined)
            return fetch(`${baseUrl}${path}`, init);
        // Through `Headers` rather than an object spread: `HeadersInit` is also
        // allowed to be an array of pairs, which a spread would quietly turn into
        // an object with numeric keys and no headers at all.
        const headers = new Headers(init?.headers);
        headers.set('authorization', `Bearer ${bearer}`);
        return fetch(`${baseUrl}${path}`, { ...init, headers });
    };
}
/**
 * What the consent log records as the source when somebody is added by hand.
 *
 * Provenance names where an address came from, and "a person typed it into the
 * contacts board" is as much an answer as a file name. It is not evidence of
 * consent and the event does not claim to be — see `ConsentEventType`.
 */
const ADDED_BY_HAND = 'Added by hand';
//# sourceMappingURL=createApiClient.js.map